// Info: BookingPanel is a reworked version of the two type components: BookingAccept (and AcceptForm) component for Multi-Leg,
// and AcceptBlock in which the form is static, for One-Way and Round-Trip bookings
// TODO: Remove BookingAccept, AcceptForm, AcceptBlock components
// when the new Driver Planner (full-calendar) will be implemented and all related components will be refactored
import { FC, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { Form, FormApi, FormState } from 'informed';
import { Spin } from 'antd';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Button } from '@ui-modules';
import { useRepository } from '@context';
import { DATE_FORMAT } from '@common/constants';
import {
  BookingTripType,
  FacilityDriverMapped,
  FacilityVehicleMapped,
  IBookingDetails,
  IBookingDrive,
  IBookingUpdate,
  IFormDriveEdit,
  IUpdateDrive,
} from '@common/interfaces';
import { useFacility } from '@common/hooks';
import { getErrorList, Serializer } from '@common/utils';
import { ArrowRound } from '@assets/svg/icons';

import { checkConflictingDrivesOnEdit } from '../../utils';
import DriveFormItem from './components/DriveFormItem/DriveFormItem';
import './BookingPanel.styles.scss';

const DATE_FORMAT_INFO_DISPLAY = 'DD MMM YYYY';
const DATE_FORMAT_INFO_FULL_DISPLAY = 'DD MMM YYYY HH:mm';

interface BookingPanelProps {
  bookingData: IBookingDetails;
  drivers: FacilityDriverMapped[];
  isEditDriveProcess?: boolean;
  vehicles: FacilityVehicleMapped[];
  onDriveCancel?: () => Promise<void>;
}

const BookingPanel: FC<BookingPanelProps> = ({
  bookingData,
  drivers,
  isEditDriveProcess,
  vehicles,
  onDriveCancel,
}) => {
  const navigate = useNavigate();
  const {
    facility: { facilityId },
  } = useFacility();
  const { bookingRepository, mobilityPlannerRepository } = useRepository();
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const [isDriveEditFormTouched, setDriveEditFormTouched] = useState(false);
  const [isVerified, setVerified] = useState(false);

  const formRef = useRef<FormApi | null>(null);

  const { mutateAsync: updateDrives } = useMutation<
    unknown,
    AxiosError,
    { bookingId: string; drives: IUpdateDrive[] }
  >(
    'update-drives',
    ({ bookingId, drives }) => mobilityPlannerRepository.updateDrive(facilityId, bookingId, drives),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: () => {
        setLoading(false);
        setVerified(false);

        toast.success(t('mobility.messageUpdateDrive'));

        if (isEditDriveProcess) {
          setTimeout(() => navigate(`/bookings/${bookingData.id}`), 500);
        }
      },
      onError: (e: any) => {
        setLoading(false);

        if (Object.values(e.response.data).length) {
          const message = getErrorList(e);
          toast.error(message.length ? message.join() : t('common.errorMessageDefault'));
        }
      },
    },
  );

  const { mutateAsync: cancelDrive } = useMutation<
    unknown,
    AxiosError,
    { bookingId: string; drives: IUpdateDrive[] }
  >(
    'cancel-drive',
    ({ bookingId, drives }) => mobilityPlannerRepository.updateDrive(facilityId, bookingId, drives),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: () => {
        setLoading(false);
        onDriveCancel?.();
        toast.success(t('mobility.messageDriveCancel'));
      },
      onError: (error) => {
        setLoading(false);

        if (error.message) {
          toast.error(t('common.errorMessageDefault'));
        }
      },
    },
  );

  const { mutateAsync: cancelBooking } = useMutation<unknown, AxiosError, IBookingUpdate>(
    'update-booking',
    (data) => bookingRepository.updateBooking(facilityId, bookingData?.id!, data),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: () => {
        setLoading(false);

        toast.success(t('bookingDetails.messageUpdateBooking'));

        if (isEditDriveProcess) {
          setTimeout(() => navigate(`/bookings/${bookingData.id}`), 500);
        }
      },
      onError: (error) => {
        setLoading(false);
        if (error.message) {
          toast.error(t('common.errorMessageDefault'));
        }
      },
    },
  );

  const { agencyFk, drives, firstName, id, lastName, pax, routes, typeOfTrip } = bookingData;

  const isOneWay = typeOfTrip === BookingTripType.OneWay;
  const isRoundTrip = typeOfTrip === BookingTripType.RoundTrip;
  const isMultiLeg = typeOfTrip === BookingTripType.MultiLeg;

  const onBtnVerifyClick = (): void => {
    const formState = formRef.current?.getFormState() as FormState;

    if (formState.errors && Object.keys(formState.errors).length) {
      toast.error(t('common.errorMessageDefault'));
    }

    const state = Object.values({
      ...(formState.values as { [key: string | number]: IFormDriveEdit }),
    });

    const activeDrives = state.filter((_, idx: number) => drives[idx].isActive);

    const isConflictingDrives = checkConflictingDrivesOnEdit(activeDrives);

    if (isConflictingDrives) {
      toast.error(t('planner.messageConflictingDrivesEdit'));
      return;
    }

    toast.success(t('planner.messageDriveVerified'));

    setVerified(true);
  };

  const activeDrives = useMemo(
    () => drives.filter((drive: IBookingDrive) => drive.isActive),
    [drives],
  );

  return (
    <Spin size="large" spinning={isLoading}>
      <section className="booking-panel">
        <ul className="booking-info">
          <li>
            <div className="label">{t('common.firstName')}</div>
            <div className="value">{firstName}</div>
          </li>
          <li>
            <div className="label">{t('common.lastName')}</div>
            <div className="value">{lastName}</div>
          </li>
          <li>
            <div className="label">{t('common.agency')}</div>
            <div className="value">{agencyFk?.shortName}</div>
          </li>
          <li>
            <div className="label">{t('common.pax')}</div>
            <div className="value">{pax}</div>
          </li>

          {!isMultiLeg && (
            <>
              <li>
                <div className="label">
                  {isRoundTrip ? `${t('bookingDetails.outward')} ` : null}
                  {t('bookingDetails.pickupAt')}
                </div>
                <div className="value">
                  <span>
                    {moment(
                      `${routes[0].pickupDate} ${routes[0].pickupTime}`,
                      DATE_FORMAT.DRIVE_DATE,
                    ).format(DATE_FORMAT_INFO_FULL_DISPLAY)}
                  </span>
                  <span>{`${routes[0].pickupTown}, ${routes[0].pickupLocation}`}</span>
                </div>
              </li>
              <li>
                <div className="label">
                  {isRoundTrip ? `${t('bookingDetails.outward')} ` : null}
                  {t('bookingDetails.dropoffAt')}
                </div>
                <div className="value">
                  <span>
                    {moment(`${routes[0].dropoffDate}`, DATE_FORMAT.API).format(
                      DATE_FORMAT_INFO_DISPLAY,
                    )}
                  </span>
                  <span>{`${routes[0].dropoffTown}, ${routes[0].dropoffLocation}`}</span>
                </div>
              </li>

              {isRoundTrip ? (
                <>
                  <li>
                    <div className="label">
                      {t('bookingDetails.return')} {t('bookingDetails.pickupAt')}
                    </div>
                    <div className="value">
                      <span>
                        {moment(
                          `${routes[1].pickupDate} ${routes[1].pickupTime}`,
                          DATE_FORMAT.DRIVE_DATE,
                        ).format(DATE_FORMAT_INFO_FULL_DISPLAY)}
                      </span>
                      <span>{`${routes[1].pickupTown}, ${routes[1].pickupLocation}`}</span>
                    </div>
                  </li>
                  <li>
                    <div className="label">
                      {t('bookingDetails.return')} {t('bookingDetails.dropoffAt')}
                    </div>
                    <div className="value">
                      <span>
                        {moment(`${routes[1].dropoffDate}`, DATE_FORMAT.API).format(
                          DATE_FORMAT_INFO_DISPLAY,
                        )}
                      </span>
                      <span>{`${routes[1].dropoffTown}, ${routes[1].dropoffLocation}`}</span>
                    </div>
                  </li>
                </>
              ) : null}
            </>
          )}

          {isMultiLeg &&
            routes.map((route, idx) => (
              <li key={route.id}>
                <div className="label">
                  {t('bookingDetails.leg')} {idx + 1}
                </div>
                <div className="value">
                  {moment(`${route.pickupDate} ${route.pickupTime}`, DATE_FORMAT.DRIVE_DATE).format(
                    DATE_FORMAT_INFO_FULL_DISPLAY,
                  )}
                </div>
              </li>
            ))}
        </ul>

        <div className="form-group">
          {isEditDriveProcess && (
            <Form
              formApiRef={formRef}
              onSubmit={(formState: Record<string, unknown>) => {
                const state = Object.values({
                  ...(formState.values as { [key: string | number]: IFormDriveEdit }),
                });

                const mappedDrives = state
                  .map((drive: IFormDriveEdit, idx: number) =>
                    Serializer.mapFormDriveEditToUpdateDrive(drive, drives[idx]),
                  )
                  .filter((drive) => drive.is_active);

                updateDrives({
                  bookingId: id,
                  drives: mappedDrives,
                });

                setDriveEditFormTouched(false);
              }}
            >
              {drivers.length && vehicles.length
                ? drives?.map((drive: IBookingDrive) => (
                    <DriveFormItem
                      bookingData={bookingData}
                      drive={drive}
                      drivers={drivers}
                      key={drive.id}
                      vehicles={vehicles}
                      onCancel={(payload) =>
                        activeDrives.length === 1
                          ? cancelBooking({
                              state: 'cancelled',
                            })
                          : cancelDrive(payload)
                      }
                      onChange={() => {
                        setDriveEditFormTouched(true);
                        setVerified(false);
                      }}
                    />
                  ))
                : null}

              <div className="form-group-footer">
                <Link className="link" to={`/bookings/${id}`}>
                  {t('bookingDetails.viewBookingDetails')}
                  <ArrowRound type="right" />
                </Link>

                {(isVerified || isOneWay) && (
                  <Button
                    className="btn btn-confirm"
                    text={t('common.btnConfirm')}
                    type="submit"
                    variant="submit"
                  />
                )}

                {!isOneWay && !isVerified && (
                  <Button
                    className="btn btn-verify"
                    disabled={!isDriveEditFormTouched}
                    text={t('common.btnVerify')}
                    variant="primary"
                    onClick={() => onBtnVerifyClick()}
                  />
                )}
              </div>
            </Form>
          )}
        </div>
      </section>
    </Spin>
  );
};

export default BookingPanel;
